/**
 * Dashboard Helpers
 *
 * Functions to build dashboard data structure from raw API data
 * All calculations, transformations, and UI-specific logic lives here
 */

import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { ActivityWithSessions } from '../../../types/GeneralTypes'
import { Sprint } from 'pure-js/types/Sprint'
import { SprintStatus } from 'pure-js/enums/SprintStatus'
import { ActivityType } from 'pure-js/enums/ActivityType'
import {
  FacilitatorDashboardData,
  DashboardInfoBox,
  DashboardSprintCard,
  DashboardActionBox
} from '../../../apiLayer/types/dashboard'
import { WeeklySessionData } from '../../../interaxDesignSystemComponents/SessionItem'
import { FACILITATOR_ACTION_BOXES } from './Dashboard.utils'

dayjs.extend(isBetween)

const LIVE_GAME_ACTIVITY_TYPES = new Set<ActivityType>([
  ActivityType.SHORT_GAME,
  ActivityType.LONG_GAME,
  ActivityType.FIRST_GAME
])

/**
 * Raw data structure returned from process handler
 */
export interface FacilitatorDashboardRawData {
  activities: ActivityWithSessions[]
  /** ACTIVE sprints only, so "vs last month" compares against sprints that are active AND from that window. */
  sprints: Sprint[]
}

/**
 * Get current week boundaries (Monday to Sunday)
 */
const getWeekBoundaries = () => {
  const now = new Date()
  const currentDay = now.getDay() // 0 = Sunday, 1 = Monday, ...
  const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1 // Convert Sunday to 6, rest to days from Monday

  const monday = new Date(now)
  monday.setDate(now.getDate() - daysFromMonday)
  monday.setHours(0, 0, 0, 0)

  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  sunday.setHours(23, 59, 59, 999)

  return { monday, sunday, now }
}

/**
 * Transform activities to this week's sessions format
 */
const buildThisWeekSessions = (activities: ActivityWithSessions[]): WeeklySessionData[] => {
  const { monday, sunday, now } = getWeekBoundaries()

  // Filter upcoming activities for this week
  const thisWeekActivities = activities
    .map((activity) => ({
      ...activity,
      activityDate: new Date(activity.startDate)
    }))
    .filter((activity) => {
      const activityDate = activity.activityDate
      // Check if activity is within this week (Monday to Sunday) and in the future or happening now
      return activityDate >= monday && activityDate <= sunday && activityDate >= now
    })

  // Sort by start date and take the next 3
  thisWeekActivities.sort((a, b) => a.activityDate.getTime() - b.activityDate.getTime())
  const next3Sessions = thisWeekActivities.slice(0, 3)

  // Transform to weekly session format
  return next3Sessions.map((activity) => {
    // Determine status from activity dates
    const currentTime = new Date()
    const startDate = activity.startDate ? new Date(activity.startDate) : null
    const endDate = activity.endDate ? new Date(activity.endDate) : null
    const isLive = !activity.endedAt && startDate && endDate && currentTime >= startDate && currentTime <= endDate
    const status = isLive ? ('live' as const) : ('upcoming' as const)

    return {
      day: activity.activityDate.getDate(),
      month: activity.activityDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      title: activity.title || 'Untitled Session',
      teams: activity.teamIds?.length || 1,
      participants: activity.participants?.length || 0,
      status,
      time: !isLive
        ? activity.activityDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        : undefined
    }
  })
}

/**
 * Transform sprints to dashboard format
 */
const buildActiveSprints = (sprints: Sprint[]): DashboardSprintCard[] => {
  return sprints
    .filter((s) => s.status === SprintStatus.ACTIVE)
    .sort((a, b) => {
      // Sort by startDate (launch date) descending (newest first)
      // If startDate is missing, fall back to createdAt
      const dateA = a.startDate ? new Date(a.startDate).getTime() : new Date(a.createdAt).getTime()
      const dateB = b.startDate ? new Date(b.startDate).getTime() : new Date(b.createdAt).getTime()
      return dateB - dateA // Descending order (newest first)
    })
    .slice(0, 2) // Limit to 2 active sprints
    .map((sprint) => ({
      id: sprint.id,
      status: 'active' as 'active' | 'draft',
      title: sprint.title || `Sprint ${sprint.purpose}`,
      details: [
        { label: 'facilitator.dashboard.sprintCard.purpose', value: sprint.purpose || 'N/A' },
        { label: 'facilitator.dashboard.sprintCard.participants', value: sprint.participants?.length || 0 },
        { label: 'facilitator.dashboard.sprintCard.teams', value: sprint.teams?.length || 0 }
      ],
      progress: 50,
      teamDetails: (sprint.teams || []).map((team) => ({
        teamName: team.name,
        memberNames: (sprint.participants || []).filter((p) => p.teamId === team.tempTeamId).map((p) => p.name)
      })),
      unassignedMemberNames: (() => {
        const assignedTeamIds = new Set((sprint.teams || []).map((t) => t.tempTeamId))
        return (sprint.participants || []).filter((p) => !p.teamId || !assignedTeamIds.has(p.teamId)).map((p) => p.name)
      })()
    }))
}

/**
 * Calculate info boxes metrics
 */
const buildInfoBoxes = (activities: ActivityWithSessions[], sprints: Sprint[]): DashboardInfoBox[] => {
  const lastMonthStart = dayjs().subtract(1, 'month').startOf('day')
  const twoMonthsAgoStart = dayjs().subtract(2, 'months').startOf('day')

  // 1. Active Sprints (vs last month)
  const activeSprints = sprints.filter((s) => s.status === SprintStatus.ACTIVE)
  const activeSprintsCount = activeSprints.length

  const lastMonthActiveSprints = sprints.filter((sprint) => {
    const createdDate = sprint.createdAt ? dayjs(sprint.createdAt) : null
    return (
      sprint.status === SprintStatus.ACTIVE &&
      createdDate &&
      createdDate.isBetween(twoMonthsAgoStart, lastMonthStart, null, '[]')
    )
  })
  const activeSprintsChange =
    lastMonthActiveSprints.length > 0
      ? Math.round(((activeSprintsCount - lastMonthActiveSprints.length) / lastMonthActiveSprints.length) * 100)
      : activeSprintsCount > 0
        ? 100
        : 0

  // 2. Live Game Sessions (currently running)
  const liveGameSessions = activities.filter((activity) => {
    const startDate = activity.startDate ? dayjs(activity.startDate) : null
    const endDate = activity.endDate ? dayjs(activity.endDate) : null
    const isCurrentlyRunning = startDate && endDate && dayjs().isBetween(startDate, endDate, null, '[]')

    return LIVE_GAME_ACTIVITY_TYPES.has(activity.type as ActivityType) && isCurrentlyRunning
  })
  const liveGameSessionsCount = liveGameSessions.length

  // 3. Total Participants (current vs last month)
  const participantSet = new Set<string>()
  sprints
    .filter((s) => s.status === SprintStatus.ACTIVE)
    .forEach((sprint) => {
      if (sprint.participants && Array.isArray(sprint.participants)) {
        sprint.participants.forEach((p: string | { userId?: string }) => {
          const userId = typeof p === 'string' ? p : p.userId
          if (userId) participantSet.add(userId)
        })
      }
    })
  const totalParticipantsCount = participantSet.size

  const lastMonthParticipantSet = new Set<string>()
  sprints
    .filter((sprint) => {
      const createdDate = sprint.createdAt ? dayjs(sprint.createdAt) : null
      return createdDate && createdDate.isBetween(twoMonthsAgoStart, lastMonthStart, null, '[]')
    })
    .forEach((sprint) => {
      if (sprint.participants && Array.isArray(sprint.participants)) {
        sprint.participants.forEach((p: string | { userId?: string }) => {
          const userId = typeof p === 'string' ? p : p.userId
          if (userId) lastMonthParticipantSet.add(userId)
        })
      }
    })
  const totalParticipantsChange =
    lastMonthParticipantSet.size > 0
      ? Math.round(((totalParticipantsCount - lastMonthParticipantSet.size) / lastMonthParticipantSet.size) * 100)
      : totalParticipantsCount > 0
        ? 100
        : 0

  // 4. Upcoming Sessions (all future sessions vs yesterday's upcoming sessions count)
  const now = dayjs()
  const upcomingSessionsCount = activities.filter((activity) => {
    const activityDate = activity.startDate ? dayjs(activity.startDate) : null
    // Count sessions that are in the future (not ended and startDate is in the future)
    return activityDate && !activity.endedAt && activityDate.isAfter(now)
  }).length

  // Calculate yesterday's upcoming sessions count for comparison
  // This represents what was "upcoming" as of yesterday (sessions starting after yesterday's start of day)
  const yesterdayStart = dayjs().subtract(1, 'day').startOf('day')
  const yesterdayUpcomingSessionsCount = activities.filter((activity) => {
    const activityDate = activity.startDate ? dayjs(activity.startDate) : null
    // Count sessions that were upcoming as of yesterday (startDate after yesterday start, not ended)
    // This gives us a baseline to compare against
    return activityDate && !activity.endedAt && activityDate.isAfter(yesterdayStart)
  }).length

  const upcomingSessionsChange =
    yesterdayUpcomingSessionsCount > 0
      ? Math.round(((upcomingSessionsCount - yesterdayUpcomingSessionsCount) / yesterdayUpcomingSessionsCount) * 100)
      : upcomingSessionsCount > 0
        ? 100
        : 0

  return [
    {
      title: 'facilitator.dashboard.infoBoxes.activeSprints',
      value: activeSprintsCount,
      change:
        activeSprintsChange !== 0
          ? `${activeSprintsChange > 0 ? '↑' : '↓'} ${Math.abs(activeSprintsChange)}%`
          : undefined,
      status: 'facilitator.dashboard.infoBoxes.status.fromLastMonth',
      changeType: activeSprintsChange > 0 ? 'increase' : activeSprintsChange < 0 ? 'decrease' : undefined
    },
    {
      title: 'facilitator.dashboard.infoBoxes.upcomingSessions',
      value: upcomingSessionsCount,
      change:
        upcomingSessionsChange !== 0
          ? `${upcomingSessionsChange > 0 ? '↑' : '↓'} ${Math.abs(upcomingSessionsChange)}%`
          : undefined,
      status: 'facilitator.dashboard.infoBoxes.status.fromYesterday',
      changeType: upcomingSessionsChange > 0 ? 'increase' : upcomingSessionsChange < 0 ? 'decrease' : undefined
    },
    {
      title: 'facilitator.dashboard.infoBoxes.gameSessionsLive',
      value: liveGameSessionsCount,
      status:
        liveGameSessionsCount > 0
          ? 'facilitator.dashboard.infoBoxes.status.liveNow'
          : 'facilitator.dashboard.infoBoxes.status.noLiveSessions',
      changeData: liveGameSessionsCount > 0 ? { count: liveGameSessionsCount } : undefined
    },
    {
      title: 'facilitator.dashboard.infoBoxes.totalParticipants',
      value: totalParticipantsCount,
      change:
        totalParticipantsChange !== 0
          ? `${totalParticipantsChange > 0 ? '↑' : '↓'} ${Math.abs(totalParticipantsChange)}%`
          : undefined,
      status: 'facilitator.dashboard.infoBoxes.status.fromLastMonth',
      changeType: totalParticipantsChange > 0 ? 'increase' : totalParticipantsChange < 0 ? 'decrease' : undefined
    }
  ]
}

/**
 * Build facilitator dashboard data from raw API data
 * This is where all dashboard-specific logic lives
 */
export const buildFacilitatorDashboardData = (
  rawData: FacilitatorDashboardRawData,
  actionBoxes?: DashboardActionBox[]
): FacilitatorDashboardData => {
  const thisWeekSessions = buildThisWeekSessions(rawData.activities)
  const activeSprints = buildActiveSprints(rawData.sprints)
  const infoBoxes = buildInfoBoxes(rawData.activities, rawData.sprints)

  return {
    actionBoxes: actionBoxes || FACILITATOR_ACTION_BOXES,
    infoBoxes,
    thisWeekSessions,
    activeSprints
  }
}
